/**
 * Makes every modal and popup in the app movable with the mouse.
 *
 * The app opens modals three different ways - react-modal portals, hand rolled
 * `*-overlay` divs, and content components rendered inside someone else's
 * modal - so nothing is wired per component here. One listener on the document
 * walks up from whatever was pressed, finds the full screen backdrop above it,
 * and takes the child sitting on that path as the panel to move. Any modal
 * added later is picked up the same way with no extra work.
 *
 * A panel is centred by flexbox, by margins, or by a -50% transform depending
 * on where it came from, so the first drag pins it down instead: measure it,
 * switch it to `position: fixed` at the exact spot it already occupies, and
 * from there move it by left/top alone. It never jumps on the first pixel.
 *
 * Touch is left alone. Modals scroll under a finger on phones and stealing
 * that gesture to drag the whole panel would break reading long forms.
 */

const DRAG_THRESHOLD_PX = 3; // travel before a press turns into a drag
const KEEP_VISIBLE_PX = 60; // panel edge that must stay on screen

// A press on a control does its own thing, it never starts a drag.
const CONTROL_SELECTOR = [
  'input',
  'textarea',
  'select',
  'option',
  'button',
  'a',
  'label',
  'summary',
  'video',
  'audio',
  'iframe',
  'canvas',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="tab"]',
  '[role="switch"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[data-no-drag]',
  '.close',
  '.chrome-picker-container',
  '[class*="picker"]',
  '[class*="resize"]',
  '[class*="slider"]',
].join(',');

// Backdrops are named for what they are: ReactModal__Overlay, popup-overlay,
// addfamily-overlay, event-modal-overlay, importdata-popup, and so on.
const BACKDROP_NAME = /(overlay|popup|modal|dialog)/i;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/**
 * True for the dimmed sheet behind a modal: fixed, and covering the viewport.
 * The name check keeps this off the hover tints and badges that also sit on
 * top of things but are only as big as the card they belong to.
 */
function isBackdrop(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (!BACKDROP_NAME.test(`${el.id} ${el.getAttribute('class') || ''}`)) return false;
  if (window.getComputedStyle(el).position !== 'fixed') return false;
  const rect = el.getBoundingClientRect();
  return (
    rect.width >= window.innerWidth * 0.9 && rect.height >= window.innerHeight * 0.9
  );
}

/**
 * The panel is the child of the backdrop that the press happened inside of.
 * Returns null for a press on the backdrop itself, which usually closes the
 * modal, and for anything that is not in a modal at all.
 */
function findPanel(node) {
  let el = node instanceof Element ? node : null;
  let child = null;
  while (el && el !== document.body) {
    if (isBackdrop(el)) return child;
    child = el;
    el = el.parentElement;
  }
  return null;
}

/**
 * Pins the panel where it currently sits so left/top become the only thing
 * deciding its position. Returns where it sits, plus the correction to add to
 * a wanted viewport position: a backdrop with a filter or a transform on it
 * becomes the containing block for its fixed children, and then the two differ.
 */
function pinInPlace(panel) {
  const before = panel.getBoundingClientRect();
  const style = panel.style;

  style.setProperty('inset', 'auto');
  style.margin = '0';
  style.transform = 'none';
  style.position = 'fixed';
  style.right = 'auto';
  style.bottom = 'auto';
  style.left = `${before.left}px`;
  style.top = `${before.top}px`;

  // Out of the flex row it was centred in, the panel can lose the width or
  // height its parent was giving it. Hand back whatever it just dropped, in
  // whichever box its own box-sizing measures a written width against.
  const after = panel.getBoundingClientRect();
  const computed = window.getComputedStyle(panel);
  const px = (value) => parseFloat(value) || 0;
  const borderBox = computed.boxSizing === 'border-box';
  if (Math.abs(after.width - before.width) > 1) {
    const outside = borderBox
      ? 0
      : px(computed.paddingLeft) + px(computed.paddingRight) +
        px(computed.borderLeftWidth) + px(computed.borderRightWidth);
    style.width = `${before.width - outside}px`;
  }
  if (Math.abs(after.height - before.height) > 1) {
    const outside = borderBox
      ? 0
      : px(computed.paddingTop) + px(computed.paddingBottom) +
        px(computed.borderTopWidth) + px(computed.borderBottomWidth);
    style.height = `${before.height - outside}px`;
  }

  const placed = panel.getBoundingClientRect();
  const correction = { x: before.left - placed.left, y: before.top - placed.top };
  if (correction.x || correction.y) {
    style.left = `${before.left + correction.x}px`;
    style.top = `${before.top + correction.y}px`;
  }
  return { left: before.left, top: before.top, correction };
}

/** Moves the panel to a viewport position, keeping an edge on screen. */
function moveTo(panel, left, top, correction) {
  const width = panel.offsetWidth;
  const height = panel.offsetHeight;
  const x = clamp(left, KEEP_VISIBLE_PX - width, window.innerWidth - KEEP_VISIBLE_PX);
  // Never above the top edge, or the header used to drag it goes out of reach.
  // Panels taller than the window may go up far enough to show their bottom.
  const y = clamp(top, Math.min(0, window.innerHeight - height), window.innerHeight - KEEP_VISIBLE_PX);
  panel.style.left = `${x + correction.x}px`;
  panel.style.top = `${y + correction.y}px`;
  return { x, y };
}

export function enableDraggableModals() {
  let panel = null; // panel under the pressed pointer, dragged or not yet
  let activePointerId = null;
  let dragging = false; // past the threshold, the panel is following the mouse
  let startX = 0;
  let startY = 0;
  let originLeft = 0;
  let originTop = 0;
  let correction = { x: 0, y: 0 };
  let hovered = null; // panel currently wearing the move cursor

  const endDrag = () => {
    if (dragging) {
      document.body.classList.remove('is-modal-dragging');
      // The press was a drag, not a click on whatever is under the pointer.
      const swallowClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
      };
      document.addEventListener('click', swallowClick, { capture: true, once: true });
      setTimeout(() => document.removeEventListener('click', swallowClick, true), 0);
    }
    panel = null;
    activePointerId = null;
    dragging = false;
  };

  const onPointerDown = (e) => {
    // Bubble phase on purpose: a component that stops propagation on its own
    // drag surface - the colour picker in the family line modal, say - opts
    // itself out without needing to be listed here.
    if (e.button !== 0 || e.pointerType === 'touch') return;
    if (!(e.target instanceof Element) || e.target.closest(CONTROL_SELECTOR)) return;

    const found = findPanel(e.target);
    if (!found) return;

    panel = found;
    activePointerId = e.pointerId;
    dragging = false;
    startX = e.clientX;
    startY = e.clientY;
  };

  const onPointerMove = (e) => {
    if (!panel || e.pointerId !== activePointerId) return;

    if (!dragging) {
      if (Math.max(Math.abs(e.clientX - startX), Math.abs(e.clientY - startY)) < DRAG_THRESHOLD_PX) {
        return;
      }
      dragging = true;
      const pinned = pinInPlace(panel);
      correction = pinned.correction;
      originLeft = pinned.left;
      originTop = pinned.top;
      document.body.classList.add('is-modal-dragging');
      window.getSelection()?.removeAllRanges();
    }

    e.preventDefault();
    moveTo(panel, originLeft + (e.clientX - startX), originTop + (e.clientY - startY), correction);
  };

  // Hovering a panel is what puts the move cursor on it, so a modal opened
  // from anywhere advertises the drag without every one of them being styled.
  const onPointerOver = (e) => {
    if (!(e.target instanceof Element)) return;
    if (hovered && hovered.isConnected && hovered.contains(e.target)) return;
    if (hovered) hovered.removeAttribute('data-modal-draggable');
    hovered = findPanel(e.target);
    if (hovered) hovered.setAttribute('data-modal-draggable', 'true');
  };

  // A window that shrinks under a modal parked near the edge would leave it
  // unreachable, so pull anything already moved back into view.
  const onResize = () => {
    document.querySelectorAll('[data-modal-moved="true"]').forEach((moved) => {
      const rect = moved.getBoundingClientRect();
      const offset = {
        x: (parseFloat(moved.style.left) || 0) - rect.left,
        y: (parseFloat(moved.style.top) || 0) - rect.top,
      };
      moveTo(moved, rect.left, rect.top, offset);
    });
  };

  const onPointerUp = (e) => {
    if (!panel || e.pointerId !== activePointerId) return;
    if (dragging) panel.setAttribute('data-modal-moved', 'true');
    endDrag();
  };

  const onPointerCancel = () => endDrag();

  document.addEventListener('pointerdown', onPointerDown);
  document.addEventListener('pointerover', onPointerOver);
  window.addEventListener('pointermove', onPointerMove, { passive: false });
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerCancel);
  window.addEventListener('resize', onResize);

  return () => {
    endDrag();
    if (hovered) hovered.removeAttribute('data-modal-draggable');
    hovered = null;
    document.removeEventListener('pointerdown', onPointerDown);
    document.removeEventListener('pointerover', onPointerOver);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerCancel);
    window.removeEventListener('resize', onResize);
  };
}

export default enableDraggableModals;
